import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Database, Mail, Bell, Landmark, CreditCard } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function TetapanAdmin() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put("/settings", { org_name: form.org_name, org_short_name: form.org_short_name, contact_email: form.contact_email, contact_phone: form.contact_phone, contact_address: form.contact_address });
      setForm(res.data);
      toast.success("Tetapan dikemas kini");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveNotifications = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try {
      const res = await api.put("/settings", {
        resend_api_key: form.resend_api_key,
        resend_from_email: form.resend_from_email,
        email_notifications_enabled: form.email_notifications_enabled,
        push_notifications_enabled: form.push_notifications_enabled,
      });
      setForm(res.data);
      toast.success("Tetapan notifikasi dikemas kini");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingEmail(false);
    }
  };

  const runLegacyImport = async () => {
    setImporting(true);
    setImportResult(null);
    try {
      const res = await api.post("/admin/import-legacy-members");
      setImportResult(res.data);
      toast.success(`Import selesai: ${res.data.imported} rekod baharu, ${res.data.skipped_existing} dilangkau (sudah wujud)`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const savePayment = async (e) => {
    e.preventDefault();
    setSavingPayment(true);
    try {
      const res = await api.put("/settings", {
        bank_name: form.bank_name,
        bank_account_number: form.bank_account_number,
        bank_account_holder: form.bank_account_holder,
        billplz_secret_key: form.billplz_secret_key,
        billplz_x_signature_key: form.billplz_x_signature_key,
        billplz_collection_id: form.billplz_collection_id,
      });
      setForm(res.data);
      toast.success("Tetapan pembayaran dikemas kini");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingPayment(false);
    }
  };

  if (!form) return <div className="text-piks-muted">Memuatkan...</div>;

  return (
    <div className="max-w-lg" data-testid="admin-tetapan-page">
      <h1 className="font-display text-2xl font-bold text-piks-black mb-1">Tetapan Sistem</h1>
      <p className="text-sm text-piks-muted mb-6">Maklumat organisasi rasmi PPPS.</p>
      <form onSubmit={save} className="bg-white rounded-xl border border-piks-border p-6 space-y-4">
        <div><Label>Nama Organisasi</Label><Input data-testid="settings-org-name" value={form.org_name || ""} onChange={(e) => setForm({ ...form, org_name: e.target.value })} className="mt-1.5" /></div>
        <div><Label>Nama Ringkas</Label><Input data-testid="settings-org-short-name" value={form.org_short_name || ""} onChange={(e) => setForm({ ...form, org_short_name: e.target.value })} className="mt-1.5" /></div>
        <div><Label>Emel Perhubungan</Label><Input data-testid="settings-contact-email" value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="mt-1.5" /></div>
        <div><Label>Telefon Perhubungan</Label><Input data-testid="settings-contact-phone" value={form.contact_phone || ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="mt-1.5" /></div>
        <div><Label>Alamat</Label><Input data-testid="settings-contact-address" value={form.contact_address || ""} onChange={(e) => setForm({ ...form, contact_address: e.target.value })} className="mt-1.5" /></div>
        <Button type="submit" disabled={saving} data-testid="settings-submit-btn" className="w-full bg-piks-black hover:bg-piks-black/90">{saving ? "Menyimpan..." : "Simpan Tetapan"}</Button>
      </form>

      <form onSubmit={saveNotifications} className="bg-white rounded-xl border border-piks-border p-6 mt-6 space-y-4" data-testid="admin-notification-settings-section">
        <h2 className="font-semibold text-piks-black mb-1 flex items-center gap-2"><Mail className="w-4 h-4" /> Notifikasi Tuntutan Keahlian</h2>
        <p className="text-sm text-piks-muted">
          Emel dihantar kepada ahli apabila mereka menghantar tuntutan keahlian, dan sekali lagi apabila admin meluluskannya.
        </p>
        <div className="flex items-center justify-between">
          <Label className="mb-0">Hantar Emel Notifikasi</Label>
          <Switch data-testid="settings-email-notifications-toggle" checked={!!form.email_notifications_enabled} onCheckedChange={(v) => setForm({ ...form, email_notifications_enabled: v })} />
        </div>
        <div>
          <Label>API Key Resend</Label>
          <Input data-testid="settings-resend-api-key" type="password" placeholder="re_xxxxxxxxxxxxxxxx" value={form.resend_api_key || ""} onChange={(e) => setForm({ ...form, resend_api_key: e.target.value })} className="mt-1.5" />
          <p className="text-xs text-piks-muted mt-1">Dapatkan API key di resend.com/api-keys. Nilai sedia ada dipaparkan bertopeng demi keselamatan.</p>
        </div>
        <div>
          <Label>Emel Penghantar</Label>
          <Input data-testid="settings-resend-from-email" value={form.resend_from_email || ""} onChange={(e) => setForm({ ...form, resend_from_email: e.target.value })} className="mt-1.5" />
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-piks-border">
          <Label className="mb-0 flex items-center gap-1.5"><Bell className="w-3.5 h-3.5" /> Notifikasi Push (PWA)</Label>
          <Switch data-testid="settings-push-notifications-toggle" checked={!!form.push_notifications_enabled} onCheckedChange={(v) => setForm({ ...form, push_notifications_enabled: v })} />
        </div>
        <Button type="submit" disabled={savingEmail} data-testid="settings-notifications-submit-btn" className="w-full bg-piks-black hover:bg-piks-black/90">{savingEmail ? "Menyimpan..." : "Simpan Notifikasi"}</Button>
      </form>

      <form onSubmit={savePayment} className="bg-white rounded-xl border border-piks-border p-6 mt-6 space-y-4" data-testid="admin-payment-settings-section">
        <h2 className="font-semibold text-piks-black mb-1 flex items-center gap-2"><CreditCard className="w-4 h-4" /> Pembayaran Kursus (Bank Transfer & Billplz)</h2>
        <p className="text-sm text-piks-muted">
          Butiran bank dipaparkan kepada pendaftar borang /kursus yang pilih "Bank Transfer". Kredential Billplz membolehkan pilihan "Billplz" berfungsi secara live.
        </p>
        <div className="pt-2 border-t border-piks-border">
          <Label className="flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5" /> Butiran Bank</Label>
        </div>
        <div><Label>Nama Bank</Label><Input data-testid="settings-bank-name" value={form.bank_name || ""} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="mt-1.5" placeholder="Contoh: Maybank" /></div>
        <div><Label>No. Akaun</Label><Input data-testid="settings-bank-account-number" value={form.bank_account_number || ""} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })} className="mt-1.5" /></div>
        <div><Label>Nama Pemegang Akaun</Label><Input data-testid="settings-bank-account-holder" value={form.bank_account_holder || ""} onChange={(e) => setForm({ ...form, bank_account_holder: e.target.value })} className="mt-1.5" /></div>
        <div className="pt-2 border-t border-piks-border">
          <Label>Billplz Secret Key</Label>
          <Input data-testid="settings-billplz-secret-key" type="password" placeholder="Dapatkan di Billplz Dashboard → Settings → Keys" value={form.billplz_secret_key || ""} onChange={(e) => setForm({ ...form, billplz_secret_key: e.target.value })} className="mt-1.5" />
        </div>
        <div>
          <Label>Billplz X-Signature Key</Label>
          <Input data-testid="settings-billplz-x-signature-key" type="password" value={form.billplz_x_signature_key || ""} onChange={(e) => setForm({ ...form, billplz_x_signature_key: e.target.value })} className="mt-1.5" />
          <p className="text-xs text-piks-muted mt-1">Aktifkan "X Signature Payment Completion" dalam Billplz dashboard untuk dapatkan key ini. Nilai sedia ada dipaparkan bertopeng demi keselamatan.</p>
        </div>
        <div><Label>Billplz Collection ID</Label><Input data-testid="settings-billplz-collection-id" value={form.billplz_collection_id || ""} onChange={(e) => setForm({ ...form, billplz_collection_id: e.target.value })} className="mt-1.5" /></div>
        <Button type="submit" disabled={savingPayment} data-testid="settings-payment-submit-btn" className="w-full bg-piks-black hover:bg-piks-black/90">{savingPayment ? "Menyimpan..." : "Simpan Tetapan Pembayaran"}</Button>
      </form>

      <div className="bg-white rounded-xl border border-piks-border p-6 mt-6" data-testid="admin-legacy-import-section">
        <h2 className="font-semibold text-piks-black mb-1 flex items-center gap-2"><Database className="w-4 h-4" /> Data Ahli Lama PPPS</h2>
        <p className="text-sm text-piks-muted mb-4">
          Import 509 rekod ahli lama (dari pangkalan data 2018) sebagai ahli "Belum Dituntut".
          Selamat ditekan berulang kali — rekod yang sudah wujud akan dilangkau secara automatik.
          Guna butang ini selepas deploy jika database production tidak menunjukkan ahli lama.
        </p>
        <Button type="button" onClick={runLegacyImport} disabled={importing} data-testid="legacy-import-btn" variant="outline" className="w-full">
          {importing ? "Mengimport..." : "Import Data Ahli Lama"}
        </Button>
        {importResult && (
          <p className="text-sm text-piks-black mt-3" data-testid="legacy-import-result">
            {importResult.imported} rekod baharu diimport • {importResult.skipped_existing} dilangkau (sudah wujud) • Jumlah: {importResult.total_records}
          </p>
        )}
      </div>
    </div>
  );
}
